// Phase P.12 — Calendar Week view (redesigned).
// Phase P.19 — added: (a) travel pill on booking tiles that carry
// `travel_from_previous`, and (b) drag-to-reschedule (PC only, HTML5 DnD,
// 15-minute snap, day + time change, immediate PATCH with revert on error).

import { useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  computeHourWindow, bookingBox, paymentTintClasses, fmtHm, fmtHmRange,
  assignOverlapLanes, laneRect, MAX_VISIBLE_LANES,
} from "@/lib/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const HOUR_PX = 44;
const SNAP_MIN = 15;   // Phase P.19 — 15-minute snap for drop targets.

function formatColHeader(d) {
  const wd = d.toLocaleDateString(undefined, { weekday: "short" });
  const dm = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
  return { weekday: wd, dm };
}

function ymdKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Phase P.19 — travel pill anchored inside a booking tile. Shows the
// "N min · X km" chip when the backend has computed a `travel_from_previous`
// block (which it only does for same-staff same-day chains, so unassigned or
// staff-swap cases silently hide).
function TravelPillChip({ travel }) {
  if (!travel) return null;
  const minutes = Math.round(Number(travel.duration_min || 0));
  const km = Number(travel.distance_km || 0);
  return (
    <span
      className="inline-flex items-center gap-0.5 mono text-[9px] uppercase tracking-wider text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-1.5 py-0 leading-tight"
      title={`Travel from previous job · ${minutes} min · ${km} km`}
      data-testid="week-travel-pill"
    >
      <span aria-hidden="true">↗</span>{minutes}m · {km}km
    </span>
  );
}

export default function CalendarWeekView({
  days,
  bookings,
  onBookingClick,
  onSlotClick,
  onPrev,
  onNext,
  onToday,
  weekLabel,
  todayYmd,
  onBookingUpdated,          // Phase P.19 — parent refetch after successful DnD.
  // Phase P.27 — day-block + notes surfaces (all optional so legacy call sites still work).
  blocksByDate,
  notesByDate,
  onBlockDay,
  onNoteClick,
}) {
  // Bucket bookings by yyyy-mm-dd so we don't scan the full list per cell.
  const byDay = useMemo(() => {
    const map = new Map();
    for (const b of bookings || []) {
      if (!b?.scheduled_start) continue;
      const d = new Date(b.scheduled_start);
      if (Number.isNaN(d.getTime())) continue;
      const key = ymdKey(d);
      const arr = map.get(key) || [];
      arr.push(b);
      map.set(key, arr);
    }
    for (const [, arr] of map) {
      arr.sort((a, b) => (a.scheduled_start || "").localeCompare(b.scheduled_start || ""));
    }
    return map;
  }, [bookings]);

  const { lo, hi } = useMemo(() => computeHourWindow(bookings || []), [bookings]);
  const hours = useMemo(() => {
    const out = [];
    for (let h = lo; h <= hi; h++) out.push(h);
    return out;
  }, [lo, hi]);
  const gridHeight = (hi - lo) * HOUR_PX;

  // ---- Drag-to-reschedule state (Phase P.19). ----------------------------
  // We keep the dragged booking id + its original ISO in refs so onDrop can
  // decide whether anything actually changed. `savingId` gates the visual
  // "saving…" halo on the tile that's mid-flight.
  const dragRef = useRef({ id: null, originalIso: "", startMs: 0, durMin: 0 });
  const [savingId, setSavingId] = useState(null);
  const [dragGhost, setDragGhost] = useState(null); // {colKey,topPx,heightPx}

  const onTileDragStart = (b) => (e) => {
    e.stopPropagation();
    dragRef.current = {
      id: b.id,
      originalIso: b.scheduled_start,
      startMs: new Date(b.scheduled_start).getTime(),
      durMin: Number(b.scheduled_duration_minutes || 0),
    };
    e.dataTransfer.effectAllowed = "move";
    // Firefox refuses to fire dragover unless setData was called at least once.
    try { e.dataTransfer.setData("text/plain", b.id); } catch { /* noop */ }
  };

  const onTileDragEnd = () => {
    dragRef.current = { id: null, originalIso: "", startMs: 0, durMin: 0 };
    setDragGhost(null);
  };

  const columnDropHandlers = (d) => {
    // Compute the snapped hour/minute from the cursor position within the
    // day column, then build a Date. Same math for dragover (ghost preview)
    // and drop (commit).
    const compute = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const y = Math.max(0, Math.min(gridHeight, e.clientY - rect.top));
      const totalMin = Math.round((y / HOUR_PX) * 60);
      const snapped = Math.round(totalMin / SNAP_MIN) * SNAP_MIN;
      const hour = lo + Math.floor(snapped / 60);
      const minute = snapped % 60;
      const next = new Date(d);
      next.setHours(hour, minute, 0, 0);
      return next;
    };

    return {
      onDragOver: (e) => {
        if (!dragRef.current.id) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        const dur = dragRef.current.durMin || 60;
        const yPx = Math.max(0, Math.min(gridHeight, e.clientY - e.currentTarget.getBoundingClientRect().top));
        // Snap ghost top too so what-you-see-is-what-you-drop.
        const totalMin = Math.round((yPx / HOUR_PX) * 60);
        const snapped = Math.round(totalMin / SNAP_MIN) * SNAP_MIN;
        const topPx = (snapped / 60) * HOUR_PX;
        setDragGhost({
          colKey: ymdKey(d),
          topPx,
          heightPx: Math.max(20, (dur / 60) * HOUR_PX),
        });
      },
      onDrop: async (e) => {
        const dragged = dragRef.current;
        setDragGhost(null);
        if (!dragged.id) return;
        e.preventDefault();
        e.stopPropagation();
        const newDate = compute(e);
        const newIso = newDate.toISOString();
        // Bail out if nothing actually changed (Date arithmetic can round to
        // the same minute after snap).
        if (Math.abs(new Date(dragged.originalIso).getTime() - newDate.getTime()) < 60_000) {
          onTileDragEnd();
          return;
        }
        const id = dragged.id;
        setSavingId(id);
        try {
          await api.put(`/bookings/${id}`, { scheduled_start: newIso });
          toast.success("Booking rescheduled");
          onBookingUpdated?.(id);
        } catch (err) {
          const msg = formatApiError(err?.response?.data?.detail) || "Reschedule rejected";
          toast.error(msg);
          // No local state to rewind — parent still holds the original list;
          // we simply skipped calling onBookingUpdated, so the tile snaps back
          // on the next natural render.
        } finally {
          setSavingId(null);
          onTileDragEnd();
        }
      },
    };
  };

  return (
    <div className="bg-card border border-border rounded-[10px] overflow-hidden" data-testid="calendar-week-view">
      <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-secondary/40">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={onPrev} data-testid="week-prev" aria-label="Previous week">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onToday} data-testid="week-today">Today</Button>
          <Button variant="outline" size="icon" onClick={onNext} data-testid="week-next" aria-label="Next week">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="text-sm font-bold" data-testid="week-header">{weekLabel}</div>
        <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground min-w-[70px] text-right">
          {(bookings || []).length} {(bookings || []).length === 1 ? "job" : "jobs"}
        </div>
      </header>

      <div
        className="grid border-b border-border bg-secondary/20"
        style={{ gridTemplateColumns: `56px repeat(7, minmax(0, 1fr))` }}
      >
        <div />
        {days.map((d) => {
          const isToday = ymdKey(d) === todayYmd;
          const { weekday, dm } = formatColHeader(d);
          // Phase P.27 — block indicator on the column header. Managers can
          // click the whole header to open the block-day modal (edit / unblock).
          const block = blocksByDate ? blocksByDate.get(ymdKey(d)) : null;
          return (
            <div
              key={dm + weekday}
              className={`px-2 py-2 border-l border-border ${isToday ? "bg-accent/10" : ""} ${block ? "bg-destructive/5" : ""} ${onBlockDay ? "cursor-pointer hover:bg-secondary/60" : ""}`}
              data-testid={`week-col-header-${ymdKey(d)}`}
              onClick={onBlockDay ? () => onBlockDay(d) : undefined}
              title={block ? `Blocked: ${block.reason || "no reason"} (click to edit)` : (onBlockDay ? "Click to block this day" : undefined)}
            >
              <div className={`text-[11px] uppercase mono tracking-widest ${isToday ? "text-accent font-black" : "text-muted-foreground"}`}>
                {weekday}
              </div>
              <div className={`text-sm font-extrabold ${isToday ? "text-accent" : ""} ${block ? "text-destructive" : ""}`}>{dm}</div>
              {block && (
                <div className="mono text-[9px] uppercase tracking-widest text-destructive/80 mt-0.5 truncate" data-testid={`week-block-badge-${ymdKey(d)}`}>
                  Blocked: {block.reason || "day off"}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div
        className="grid"
        style={{ gridTemplateColumns: `56px repeat(7, minmax(0, 1fr))`, height: gridHeight }}
      >
        <div className="border-r border-border" style={{ height: gridHeight }}>
          {hours.map((h) => (
            <div
              key={h}
              className="mono text-[10px] uppercase tracking-widest text-muted-foreground pr-2 text-right border-b border-border/60 flex items-start justify-end pt-0.5"
              style={{ height: HOUR_PX }}
            >
              {String(h).padStart(2, "0")}:00
            </div>
          ))}
        </div>

        {days.map((d) => {
          const key = ymdKey(d);
          const isToday = key === todayYmd;
          const dayItems = byDay.get(key) || [];
          const dropHandlers = columnDropHandlers(d);
          // Phase P.27 — column tint + inline note chips for this day.
          const block = blocksByDate ? blocksByDate.get(key) : null;
          const dayNotes = notesByDate ? (notesByDate.get(key) || []) : [];
          return (
            <div
              key={key}
              className={`relative border-l border-border ${isToday ? "bg-accent/5" : ""} ${block ? "bg-destructive/[0.04]" : ""}`}
              onClick={(e) => {
                if (e.target && e.target.closest?.("[data-booking-tile]")) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const y = e.clientY - rect.top;
                const hour = lo + Math.floor(y / HOUR_PX);
                const slot = new Date(d);
                slot.setHours(hour, 0, 0, 0);
                onSlotClick?.(slot);
              }}
              onDragOver={dropHandlers.onDragOver}
              onDrop={dropHandlers.onDrop}
              data-testid={`week-col-${key}`}
            >
              {hours.map((h, idx) => (
                <div
                  key={h}
                  className="absolute left-0 right-0 border-b border-border/40 pointer-events-none"
                  style={{ top: idx * HOUR_PX, height: HOUR_PX }}
                />
              ))}
              {/* Phase P.19 — drop-target ghost. */}
              {dragGhost && dragGhost.colKey === key && (
                <div
                  className="absolute left-1 right-1 border-2 border-dashed border-accent bg-accent/10 rounded-[6px] pointer-events-none z-10"
                  style={{ top: dragGhost.topPx, height: dragGhost.heightPx }}
                  data-testid="week-drop-ghost"
                />
              )}
              {(() => {
                const laned = assignOverlapLanes(dayItems);
                const nodes = [];
                const overflowByKey = new Map();
                laned.forEach((x) => {
                  if (x.laneCount > MAX_VISIBLE_LANES && x.lane >= MAX_VISIBLE_LANES - 1) {
                    const k = `${x.laneCount}-${new Date(x.b.scheduled_start).toISOString().slice(0, 16)}`;
                    const arr = overflowByKey.get(k) || [];
                    arr.push(x);
                    overflowByKey.set(k, arr);
                  }
                });
                laned.forEach((x) => {
                  const { b, lane, laneCount } = x;
                  const isOverflowItem = laneCount > MAX_VISIBLE_LANES && lane >= MAX_VISIBLE_LANES - 1;
                  if (isOverflowItem) return;
                  const { top, height } = bookingBox(b, { lo, hourPx: HOUR_PX });
                  const { leftPct, widthPct } = laneRect(lane, laneCount);
                  const tint = paymentTintClasses(b);
                  const isSaving = savingId === b.id;
                  nodes.push(
                    <button
                      key={b.id}
                      type="button"
                      draggable
                      onDragStart={onTileDragStart(b)}
                      onDragEnd={onTileDragEnd}
                      data-booking-tile
                      data-testid={`week-booking-${b.id}`}
                      onClick={(e) => { e.stopPropagation(); onBookingClick?.(b.id); }}
                      className={`absolute text-left rounded-[6px] border border-l-4 ${tint.border} ${tint.bg} px-2 py-1 hover:shadow-md transition-shadow overflow-hidden cursor-grab active:cursor-grabbing ${isSaving ? "opacity-70 ring-2 ring-accent/60" : ""}`}
                      style={{
                        top, height,
                        left: `calc(${leftPct}% + 4px)`,
                        width: `calc(${widthPct}% - 8px)`,
                      }}
                      title={`${b.customer_name || ""} · ${fmtHm(b.scheduled_start)}${b.staff_name ? ` · ${b.staff_name}` : ""} · Drag to reschedule`}
                    >
                      <div className="flex items-start justify-between gap-1">
                        <div className="text-[11px] font-extrabold truncate flex-1">{b.customer_name || "—"}</div>
                        {isSaving ? (
                          <Loader2 className="h-3 w-3 animate-spin text-accent shrink-0" aria-label="Saving" />
                        ) : (
                          <span className={`inline-block h-2 w-2 rounded-full mt-0.5 ${tint.dot} shrink-0`} title={tint.label} />
                        )}
                      </div>
                      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        {fmtHmRange(b)}
                      </div>
                      {/* Phase P.19 — travel from previous job (same staff same day). */}
                      {b.travel_from_previous && height > 34 && (
                        <div className="mt-0.5">
                          <TravelPillChip travel={b.travel_from_previous} />
                        </div>
                      )}
                      {height > 60 && b.staff_name && (
                        <div className="text-[10px] text-muted-foreground truncate">{b.staff_name}</div>
                      )}
                      {height > 44 && Array.isArray(b.template_ids) && b.template_ids.length > 1 && (
                        <div
                          className="text-[10px] font-bold text-amber-800 bg-amber-100 border border-amber-200 rounded px-1 py-0.5 inline-flex items-center gap-0.5 mt-1"
                          data-testid={`booking-multi-template-badge-${b.id}`}
                          title={(b.job_templates || []).map((t) => t.name).join(", ")}
                        >
                          +{b.template_ids.length - 1} services ⚠
                        </div>
                      )}
                    </button>
                  );
                });
                overflowByKey.forEach((group, k) => {
                  const anchor = group[0];
                  const { top, height } = bookingBox(anchor.b, { lo, hourPx: HOUR_PX });
                  const { leftPct, widthPct } = laneRect(anchor.lane, anchor.laneCount);
                  nodes.push(
                    <Popover key={`overflow-${k}`}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-booking-tile
                          data-testid={`week-overflow-${k}`}
                          onClick={(e) => e.stopPropagation()}
                          className="absolute text-left rounded-[6px] border border-dashed border-slate-400 bg-slate-100 px-1 py-1 hover:bg-slate-200 transition-colors overflow-hidden flex items-center justify-center"
                          style={{
                            top, height,
                            left: `calc(${leftPct}% + 4px)`,
                            width: `calc(${widthPct}% - 8px)`,
                          }}
                        >
                          <div className="text-center">
                            <div className="text-[11px] font-black text-slate-700">+{group.length}</div>
                            <div className="mono text-[8px] uppercase tracking-widest text-slate-500">more</div>
                          </div>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64" align="start">
                        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                          // {group.length} more at this slot
                        </div>
                        <div className="space-y-1.5 max-h-72 overflow-y-auto">
                          {group.map((x) => (
                            <button
                              key={x.b.id}
                              type="button"
                              onClick={() => onBookingClick?.(x.b.id)}
                              className="w-full text-left border border-border rounded p-2 hover:bg-secondary/60"
                              data-testid={`week-overflow-item-${x.b.id}`}
                            >
                              <div className="text-xs font-bold truncate">{x.b.customer_name || "—"}</div>
                              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                                {fmtHmRange(x.b)}{x.b.staff_name ? ` · ${x.b.staff_name}` : ""}
                              </div>
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  );
                });
                // Phase P.27 — note chips inside this column at their exact
                // minute position. Same amber palette as DayView.
                dayNotes.forEach((n) => {
                  const t = new Date(n.at);
                  if (Number.isNaN(t.getTime())) return;
                  const minutesFromLo = (t.getHours() - lo) * 60 + t.getMinutes();
                  if (minutesFromLo < 0 || minutesFromLo > (hi - lo) * 60) return;
                  const top = (minutesFromLo / 60) * HOUR_PX;
                  nodes.push(
                    <button
                      key={`note-${n.id}`}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onNoteClick?.(n); }}
                      data-testid={`week-note-${n.id}`}
                      data-booking-tile
                      className="absolute right-1 rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 px-1.5 py-0.5 shadow-sm text-left overflow-hidden"
                      style={{ top, height: 22, maxWidth: "70%" }}
                      title={n.note}
                    >
                      <div className="text-[10px] font-medium truncate leading-tight">
                        {fmtHm(t)} · {n.note}
                      </div>
                    </button>
                  );
                });
                return nodes;
              })()}
            </div>
          );
        })}
      </div>
    </div>
  );
}
